import {
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useSyncExternalStore,
} from 'react';
import { flushSync } from 'react-dom';
import { prefersReducedMotion } from './platform';

/**
 * A small history router: path patterns, overlay routes (sheets that keep the screen
 * underneath), view transitions with a direction, and scroll restoration per entry.
 */

export type RouteName =
  | 'trips'
  | 'search'
  | 'trip'
  | 'plan'
  | 'newPlan'
  | 'editPlan'
  | 'flight'
  | 'flights'
  | 'wallet'
  | 'news'
  | 'brief'
  | 'walletCard'
  | 'walletProgram'
  | 'walletHistory'
  | 'walletNew'
  | 'walletEdit'
  | 'map'
  | 'flightMap'
  | 'stats'
  | 'inbox'
  | 'source'
  | 'settings'
  | 'settingsPage'
  | 'setup'
  | 'signin'
  | 'pair';

export type Tab = 'trips' | 'flights' | 'wallet' | 'news' | 'stats';

interface RouteDef {
  name: RouteName;
  pattern: RegExp;
  keys: string[];
  tab: Tab | null;
  /** Overlays render on top of another screen. */
  overlay: boolean;
}

function def(name: RouteName, path: string, tab: Tab | null, overlay = false): RouteDef {
  const keys: string[] = [];
  const pattern = new RegExp(
    `^${path.replace(/:([A-Za-z]+)/g, (_, k: string) => {
      keys.push(k);
      return '([^/]+)';
    })}/?$`,
  );
  return { name, pattern, keys, tab, overlay };
}

const ROUTES: RouteDef[] = [
  def('trips', '/', 'trips'),
  def('search', '/search', 'trips'),
  def('trip', '/trips/:tripId', 'trips'),
  def('plan', '/trips/:tripId/plans/:itemId', 'trips', true),
  def('newPlan', '/new', null, true),
  def('editPlan', '/plans/:itemId/edit', null, true),
  def('flights', '/flights', 'flights'),
  def('flight', '/flights/:itemId', 'flights'),
  def('wallet', '/wallet', 'wallet'),
  def('walletCard', '/wallet/cards/:recordId', 'wallet'),
  def('walletProgram', '/wallet/points/:recordId', 'wallet'),
  def('walletHistory', '/wallet/history', 'wallet'),
  def('walletNew', '/wallet/new/:kind', null, true),
  def('walletEdit', '/wallet/edit/:kind/:recordId', null, true),
  def('map', '/map', null),
  def('news', '/news', 'news'),
  def('brief', '/news/:briefId', 'news'),
  def('stats', '/stats', 'stats'),
  def('flightMap', '/stats/map', 'stats'),
  def('inbox', '/inbox', null),
  def('source', '/inbox/:sourceId', null),
  def('settings', '/settings', null),
  def('settingsPage', '/settings/:page', null),
  def('setup', '/setup', null),
  def('signin', '/signin', null),
  def('pair', '/pair', null),
];

export interface Route {
  name: RouteName;
  params: Record<string, string>;
  query: URLSearchParams;
  tab: Tab | null;
  overlay: boolean;
  path: string;
}

interface EntryState {
  key: string;
  idx: number;
  /** Where an overlay was opened from. */
  background?: string;
}

export function match(url: string): Route {
  const u = new URL(url, 'http://x');
  for (const r of ROUTES) {
    const m = r.pattern.exec(u.pathname);
    if (m) {
      const params: Record<string, string> = {};
      r.keys.forEach((k, i) => {
        params[k] = decodeURIComponent(m[i + 1]!);
      });
      return {
        name: r.name,
        params,
        query: u.searchParams,
        tab: r.tab,
        overlay: r.overlay,
        path: u.pathname + u.search,
      };
    }
  }
  return {
    name: 'trips',
    params: {},
    query: u.searchParams,
    tab: 'trips',
    overlay: false,
    path: '/',
  };
}

const randKey = () => Math.random().toString(36).slice(2, 10);

interface Snapshot {
  route: Route;
  background: Route | null;
  idx: number;
  key: string;
}

/**
 * The preview build is hosted at a path the router doesn't own, so it keeps app paths in
 * the hash (`#/trips/trp_mpls`). Everywhere else they're real paths.
 */
function currentUrl(): string {
  if (__PREVIEW__) return location.hash.slice(1) || '/';
  return location.pathname + location.search;
}

export function hrefFor(to: string): string {
  return __PREVIEW__ ? `#${to}` : to;
}

function read(): Snapshot {
  const st = (history.state ?? {}) as Partial<EntryState>;
  const route = match(currentUrl());
  return {
    route,
    background: route.overlay && st.background ? match(st.background) : null,
    idx: st.idx ?? 0,
    key: st.key ?? 'root',
  };
}

let snapshot: Snapshot;
const listeners = new Set<() => void>();
const scrollPositions = new Map<string, number>();

function emit() {
  for (const l of listeners) l();
}

export function initRouter() {
  if (!(history.state as EntryState | null)?.key) {
    history.replaceState({ key: randKey(), idx: 0 } satisfies EntryState, '');
  }
  history.scrollRestoration = 'manual';
  snapshot = read();
  window.addEventListener('popstate', () => {
    const prev = snapshot;
    const next = read();
    const kind =
      prev.route.overlay || next.route.overlay ? 'none' : next.idx < prev.idx ? 'pop' : 'push';
    transition(kind, () => {
      scrollPositions.set(prev.key, window.scrollY);
      snapshot = next;
    });
  });
}

export type TransitionKind = 'push' | 'pop' | 'fade' | 'none';

function transition(kind: TransitionKind, update: () => void) {
  const apply = () => {
    flushSync(() => {
      update();
      emit();
    });
    window.scrollTo(0, scrollPositions.get(snapshot.key) ?? 0);
  };
  const doc = document as Document & {
    startViewTransition?: (arg: { update: () => void; types?: string[] }) => unknown;
  };
  if (
    kind === 'none' ||
    !doc.startViewTransition ||
    prefersReducedMotion() ||
    document.visibilityState !== 'visible'
  ) {
    apply();
    return;
  }
  doc.startViewTransition({ update: apply, types: [kind] });
}

export interface NavigateOptions {
  replace?: boolean;
  /** Open as an overlay on top of the current screen. */
  overlay?: boolean;
  transition?: TransitionKind;
}

export function navigate(to: string, opts: NavigateOptions = {}) {
  const prev = snapshot;
  const target = match(to);
  const overlay = opts.overlay ?? target.overlay;
  const background = overlay
    ? prev.route.overlay
      ? ((history.state as EntryState | null)?.background ?? '/')
      : prev.route.path
    : undefined;
  const state: EntryState = {
    key: randKey(),
    idx: opts.replace ? prev.idx : prev.idx + 1,
    background,
  };
  const kind = opts.transition ?? (overlay || prev.route.overlay ? 'none' : 'push');
  transition(kind, () => {
    scrollPositions.set(prev.key, window.scrollY);
    if (opts.replace) history.replaceState(state, '', hrefFor(to));
    else history.pushState(state, '', hrefFor(to));
    snapshot = read();
    if (!opts.replace) scrollPositions.delete(snapshot.key);
  });
}

/** Goes back, or to `fallback` when there's nothing to go back to (deep link). */
export function back(fallback = '/') {
  if (snapshot.idx > 0) history.back();
  else navigate(fallback, { replace: true, transition: 'pop' });
}

export function canGoBack() {
  return snapshot.idx > 0;
}

export function useRouter(): Snapshot {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => snapshot,
    () => snapshot,
  );
}

export function currentRoute(): Route {
  return snapshot.route;
}

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  replace?: boolean;
  overlay?: boolean;
  transition?: TransitionKind;
  children: ReactNode;
}

export function Link({
  to,
  replace,
  overlay,
  transition: kind,
  onClick,
  children,
  ...rest
}: LinkProps) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
      return;
    e.preventDefault();
    navigate(to, { replace, overlay, transition: kind });
  };
  return (
    <a href={hrefFor(to)} onClick={handle} {...rest}>
      {children}
    </a>
  );
}
